import React, { useState } from 'react';
import {
  IonPage,
  IonContent,
  IonItem,
  IonText,
  IonRadioGroup,
  IonRadio,
  useIonViewWillLeave,
} from '@ionic/react';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { ProfileWizardLogic } from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';
import { pxToRem } from '@/modules/config/constants';

type ProofMethod = 'old-phone' | 'email';

/**
 * PhoneMigrationProofStep (#441): the single entry point for proving control of
 * the existing account before switching numbers. The caller picks HOW they want
 * to prove it's them; each method collects its own inputs on dedicated steps:
 *   1. old-phone → a code texted to the old number (PhoneMigrationOldPhoneStep)
 *   2. email     → a code to the email address on file for the old number
 *                  (PhoneMigrationEmailStep), then the knowledge-based security
 *                  questions (PhoneMigrationQuestionsStep). Both factors are
 *                  required: neither inbox access nor the answers alone may
 *                  move an account onto a new phone.
 * Whichever method succeeds lands on the new-phone step to finish switching.
 */
const PhoneMigrationProofStep: React.FC = () => {
  const { navigateWithOrigin, navigateToStep, progress } =
    useProfileWizardContext();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // Default to the old-phone code — the simplest proof for anyone who still has
  // their old number.
  const [method, setMethod] = useState<ProofMethod>('old-phone');

  const handleNext = () => {
    const route =
      method === 'email'
        ? '/profile/steps/phone-migration-email'
        : '/profile/steps/phone-migration-old-phone';
    navigateWithOrigin(route, 'forward');
  };

  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('phone-migration-proof')}
        progress={progress}
        onBack={() =>
          navigateToStep(
            ProfileWizardLogic.previousStep('phone-migration-proof'),
            'back'
          )
        }
      />
      <IonContent>
        <div style={{ padding: '20px 20px 120px' }}>
          <IonText>
            <p style={{ fontSize: pxToRem(16), marginBottom: '10px' }}>
              After you relink you can't use your old number to see your stats
              or edit actions, so we want to double check your identity.
            </p>
            <p
              style={{
                fontSize: pxToRem(14),
                margin: '0 0 8px',
              }}
            >
              How do you want to verify?
            </p>
          </IonText>

          <IonRadioGroup
            value={method}
            onIonChange={e => setMethod(e.detail.value as ProofMethod)}
          >
            <IonItem>
              <IonRadio
                value="old-phone"
                labelPlacement="end"
                justify="start"
                data-testid="pm-proof-old-phone"
              >
                With code to old phone
              </IonRadio>
            </IonItem>
            <IonItem>
              <IonRadio
                value="email"
                labelPlacement="end"
                justify="start"
                data-testid="pm-proof-email"
              >
                With code to email address and some questions
              </IonRadio>
            </IonItem>
          </IonRadioGroup>
        </div>
      </IonContent>
      <FooterDock background="transparent">
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            onClick={handleNext}
            color="primary"
            data-testid="profile-wizard-next"
            style={{ margin: 0 }}
          >
            Next
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>
    </IonPage>
  );
};

export default PhoneMigrationProofStep;
